import { useState } from 'react';
import { useCanAccess, useNotify, useRecordContext } from 'ra-core';
import { lireLeReleveDunCours } from '@groupe-trousseau/shared/api-client';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { deballer, lireLErreur } from '../../fournisseurs/client';

type Cours = { id: string; titre: string };

type Ligne = {
  personneId: string;
  nom: string | null;
  prenom: string | null;
  role: 'TUTOR' | 'STUDENT';
  structureId: string;
  entreeA: string;
  sortieA: string | null;
  causeDeSortie:
    | 'LEFT_BY_SELF'
    | 'DISCONNECTED'
    | 'INACTIVITY'
    | 'ACCESS_REVOKED'
    | 'COURSE_ENDED'
    | null;
  dureeSecondes: number | null;
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE RELEVÉ DES ENTRÉES ET DES SORTIES — D-084, ET C'EST CE QUI EXPLIQUE UNE DURÉE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * §6.14 l'accorde aux **deux** rôles : « les lignes des cours que sa structure porte » pour
 * l'`orgadmin`, « tout le parc » pour le compte qui voit tout. Le droit est confirmé depuis
 * le 20260824, **et aucune route ne le rendait** — c'est la route de D-084, écrite le
 * 20260830 avec cet écran.
 *
 * **CE QU'IL SERT, ET LE MOTIF EST ÉCRIT À D-084.** « La liste des cours affiche une durée,
 * et **ces lignes sont ce qui la compose**. Sans elles, une personne qui administre voit un
 * nombre de minutes et **n'a aucun moyen de savoir d'où il vient** — sur la seule surface du
 * produit dont un chiffre est la base d'une facturation qui se fait entièrement hors du
 * produit. **Un total qu'on ne peut pas décomposer et qui sert à facturer est exactement ce
 * qu'une structure viendra contester.** »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **IL VIT SUR LA LIGNE DU COURS, ET C'EST F-63 QUI RANGE LES GESTES AINSI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « **Deux des dix écrans rendus ne sont pas des écrans** » (F-62) : ce sont des gestes sur
 * la ligne. Celui-ci en est un cinquième, et il n'ouvre aucune adresse — il déplie ce que la
 * durée affichée juste à côté de lui contient.
 *
 * **La borne n'est pas testée ici, et c'est une règle de ce dossier.** « Une table côté
 * navigateur ne protège rien » : la matrice décide ce qui s'affiche, l'autorisation vit sur
 * la route. Le refus arrive donc nommé, `AUCUN_ELEVE_DE_CETTE_STRUCTURE`, et l'écran le dit
 * dans les mots de la règle plutôt que de le traduire.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **LA SOMME DES DURÉES DE LIGNES N'EST PAS LA DURÉE DU COURS, ET LE DIRE EST NÉCESSAIRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * La durée d'un cours est une **co-présence sur des segments qui se recouvrent** ; la durée
 * d'une ligne est le temps d'une personne. F-46 le pose pour l'historique : « **sur un cours
 * groupé, elles diffèrent pour presque tout le monde** ». Un écran qui additionnerait cette
 * colonne et la comparerait à la durée du cours trouverait un écart **qui est la règle**, et
 * quelqu'un finirait par « corriger » l'un des deux calculs. **L'écran ne totalise donc rien**
 * et dit pourquoi.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CINQ CAUSES DE SORTIE, LISTE FERMÉE — ET ELLES SE TRADUISENT ICI ET NULLE PART AILLEURS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * C'est la cause qui « distingue **sortir de son poste** de **perdre le transport temps
 * réel** », confusion que le cadrage interdit. La table de correspondance est **exhaustive
 * par le type** : une sixième cause au modèle ne compilerait pas ici, ce qui est le seul
 * moyen d'éviter qu'elle s'affiche en anglais sans que personne le voie.
 *
 * **Une ligne encore ouverte porte une durée VIDE, jamais zéro** — zéro dirait « personne
 * n'est resté », là où la ligne dit « personne n'est encore sorti ». L'écran écrit
 * « en cours » plutôt qu'un tiret, qui se lirait comme une donnée manquante.
 */

/** Les cinq causes de `ExitCause`, traduites. **Exhaustive par le type.** */
const CAUSE: Record<NonNullable<Ligne['causeDeSortie']>, string> = {
  LEFT_BY_SELF: 'partie d’elle-même',
  DISCONNECTED: 'sortie sur une coupure',
  INACTIVITY: 'déconnectée faute d’activité',
  ACCESS_REVOKED: 'accès annulé',
  COURSE_ENDED: 'fin du cours',
};

const ROLE: Record<Ligne['role'], string> = {
  TUTOR: 'tuteur',
  STUDENT: 'élève',
};

/** Une durée en secondes, telle qu'une personne la lit. Zéro se dit, il ne se cache pas. */
function dureeLisible(secondes: number): string {
  if (secondes <= 0) return '0 min';
  const heures = Math.floor(secondes / 3600);
  const minutes = Math.floor((secondes % 3600) / 60);
  if (heures === 0) return `${minutes} min`;
  return `${heures} h ${String(minutes).padStart(2, '0')}`;
}

function instantLisible(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

export function ReleveDunCours() {
  const cours = useRecordContext<Cours>();
  const notifier = useNotify();
  const { canAccess: peutLire } = useCanAccess({
    resource: 'releves',
    action: 'show',
  });

  const [lignes, setLignes] = useState<Ligne[] | null>(null);
  const [suite, setSuite] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  if (cours === undefined || !peutLire) return null;

  /**
   * **La tranche suivante s'AJOUTE, elle ne remplace pas.** Un relevé se lit d'un bout à
   * l'autre pour expliquer une durée : remplacer la tranche ferait perdre les lignes déjà
   * lues, et c'est leur ensemble qui compose le nombre qu'on cherche à comprendre.
   */
  const charger = async (depuis: string | null) => {
    setEnCours(true);
    try {
      const tranche = await deballer(
        lireLeReleveDunCours({
          path: { id: cours.id },
          query: depuis === null ? {} : { suite: depuis },
        }),
      );
      setLignes((precedentes) => [...(precedentes ?? []), ...tranche.lignes]);
      setSuite(tranche.suite);
    } catch (erreur) {
      const lue = lireLErreur(erreur);
      notifier(
        lue.code === 'AUCUN_ELEVE_DE_CETTE_STRUCTURE'
          ? 'Aucun élève de votre structure ne figure sur ce cours : vous ne lisez le relevé ' +
              'que des cours où l’un des vôtres se trouve.'
          : lue.message,
        { type: 'error' },
      );
    } finally {
      setEnCours(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={enCours}
        onClick={() => void charger(null)}
      >
        Relevé
      </Button>

      {lignes !== null && (
        <Alert className="mt-2">
          <AlertTitle>
            Relevé des entrées et des sorties — {lignes.length} ligne(s)
          </AlertTitle>
          <AlertDescription>
            {lignes.length === 0 ? (
              /**
               * **Un relevé vide n'est pas une panne, et l'écran le dit.** « Un cours où
               * personne n'est jamais entré porte sa ligne, avec sa date, ses participants,
               * **une durée de zéro** » (F-46) : c'est exactement ce cours-là, vu par son
               * relevé. Un encart vide se lirait comme un défaut de chargement.
               */
              <span className="block">
                Personne n’est jamais entré dans ce cours. Sa durée est donc de
                zéro, et ce n’est pas une donnée manquante.
              </span>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr>
                      <th className="pr-4 font-medium">Personne</th>
                      <th className="pr-4 font-medium">Rôle sur ce cours</th>
                      <th className="pr-4 font-medium">Entrée</th>
                      <th className="pr-4 font-medium">Sortie</th>
                      <th className="pr-4 font-medium">Cause</th>
                      <th className="font-medium">Durée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((ligne) => (
                      <tr
                        key={`${ligne.personneId}-${ligne.entreeA}`}
                        className="align-top"
                      >
                        <td className="pr-4">
                          {/* **Un compte anonymisé sort SANS NOM**, et l'écran ne dit ni
                              la cause ni la date — F-46 règle 2. Le relevé, lui, reste :
                              « la somme des minutes compte les cours des comptes
                              anonymisés, et elle ne baisse jamais rétroactivement ». */}
                          {ligne.prenom === null && ligne.nom === null
                            ? 'Sans nom'
                            : `${ligne.prenom ?? ''} ${ligne.nom ?? ''}`.trim()}
                        </td>
                        <td className="pr-4">{ROLE[ligne.role]}</td>
                        <td className="pr-4">
                          {instantLisible(ligne.entreeA)}
                        </td>
                        <td className="pr-4">
                          {ligne.sortieA === null
                            ? 'encore présente'
                            : instantLisible(ligne.sortieA)}
                        </td>
                        <td className="pr-4">
                          {ligne.causeDeSortie === null
                            ? '—'
                            : CAUSE[ligne.causeDeSortie]}
                        </td>
                        <td>
                          {ligne.dureeSecondes === null
                            ? 'en cours'
                            : dureeLisible(ligne.dureeSecondes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <span className="mt-3 block text-xs">
              Le <strong>rôle</strong> et la structure de chaque ligne sont ceux
              du cours, figés à l’entrée — jamais ceux du compte aujourd’hui.{' '}
              <strong>
                La somme de la colonne « Durée » n’est pas la durée du cours
              </strong>{' '}
              : celle-ci est une co-présence sur des segments qui se recouvrent,
              et sur un cours groupé les deux diffèrent pour presque tout le
              monde. Parcourir ce relevé ne laisse aucune ligne de trace.
            </span>

            {suite !== null && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                disabled={enCours}
                onClick={() => void charger(suite)}
              >
                Lignes suivantes
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
